import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, Plus, QrCode, Copy, Check, Storefront, Trash, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandsPage() {
  const [brands, setBrands] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [newBrand, setNewBrand] = useState({ name: "", code: "", location: "" });
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const fetchBrands = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/brands?${params.toString()}`);
      setBrands(res.data.brands);
      setTotal(res.data.total);
    } catch (error) {
      toast.error("Failed to load brands");
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const handleSearch = (e) => { e.preventDefault(); fetchBrands(); };

  const handleAddBrand = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/brands`, newBrand);
      toast.success("Brand added successfully");
      setShowAddDialog(false);
      setNewBrand({ name: "", code: "", location: "" });
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add brand");
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm("Are you sure you want to delete this brand?")) return;
    try {
      await axios.delete(`${API}/brands/${brandId}`);
      toast.success("Brand deleted");
      fetchBrands();
    } catch (error) {
      toast.error("Failed to delete brand");
    }
  };

  const showQR = async (brand) => {
    try {
      const res = await axios.get(`${API}/brands/${brand.id}/qr`);
      setQrData(res.data);
      setSelectedBrand(brand);
      setShowQRDialog(true);
    } catch (error) {
      toast.error("Failed to generate QR");
    }
  };

  const copyLink = () => {
    if (qrData?.whatsapp_link) {
      navigator.clipboard.writeText(qrData.whatsapp_link);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadQR = async (brand) => {
    try {
      const res = await axios.get(`${API}/brands/${brand.id}/qr-download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `QR_${brand.name.replace(/\s/g, "_")}_${brand.code}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("QR code downloaded!");
    } catch (error) {
      toast.error("Failed to download QR code");
    }
  };

  const downloadQRFromDialog = () => {
    if (selectedBrand) downloadQR(selectedBrand);
  };

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">Brands</h2>
          <p className="text-[#64748B] text-sm mt-1">Manage brands and generate QR codes</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#001F6E] gap-2" data-testid="add-brand-btn"><Plus size={18} />Add Brand</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add New Brand</DialogTitle></DialogHeader>
            <form onSubmit={handleAddBrand} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Brand Name *</Label>
                <Input placeholder="e.g., Zara" value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} required data-testid="new-brand-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Brand Code * (for QR)</Label>
                <Input placeholder="e.g., ZARA01" value={newBrand.code} onChange={(e) => setNewBrand({ ...newBrand, code: e.target.value.toUpperCase() })} required data-testid="new-brand-code" />
                <p className="text-xs text-[#94A3B8]">Short unique code used in QR messages</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Location</Label>
                <Input placeholder="e.g., Ground Floor, Shop 12" value={newBrand.location} onChange={(e) => setNewBrand({ ...newBrand, location: e.target.value })} data-testid="new-brand-location" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-[#002FA7] hover:bg-[#001F6E]" data-testid="submit-new-brand">Add Brand</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input placeholder="Search by name or code..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="brand-search-input" />
            </div>
            <Button type="submit" variant="secondary" data-testid="brand-search-btn">Search</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0]">
        <CardHeader className="border-b border-[#E2E8F0] pb-4">
          <CardTitle className="font-heading text-lg flex items-center justify-between">
            <span className="flex items-center gap-2"><Storefront size={24} className="text-[#002FA7]" />Brand List</span>
            <span className="text-sm font-normal text-[#64748B]">{total} brands</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-[#64748B]">Loading...</div>
          ) : brands.length === 0 ? (
            <div className="p-8 text-center text-[#64748B]">No brands found. Add your first brand to generate QR codes.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-[#F8FAFC]">
                    <TableHead>Brand Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brands.map((brand) => (
                    <TableRow key={brand.id} className="table-row-hover" data-testid={`brand-row-${brand.id}`}>
                      <TableCell className="font-medium">{brand.name}</TableCell>
                      <TableCell><Badge className="bg-[#DBEAFE] text-[#1E40AF] font-mono">{brand.code}</Badge></TableCell>
                      <TableCell className="text-[#64748B]">{brand.location || "-"}</TableCell>
                      <TableCell><Badge className={brand.is_active ? "status-available" : "status-expired"}>{brand.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                      <TableCell className="font-mono text-sm text-[#64748B]">{brand.created_at ? format(new Date(brand.created_at), "MMM dd, yyyy") : "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => showQR(brand)} className="gap-1 text-[#002FA7]" data-testid={`qr-brand-${brand.id}`}>
                            <QrCode size={16} />QR
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => downloadQR(brand)} className="gap-1 text-[#10B981]" data-testid={`download-qr-${brand.id}`}>
                            <DownloadSimple size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteBrand(brand.id)} className="gap-1 text-red-500 hover:text-red-700" data-testid={`delete-brand-${brand.id}`}>
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <QrCode size={24} className="text-[#002FA7]" />
              QR Code - {selectedBrand?.name}
            </DialogTitle>
          </DialogHeader>
          {qrData && (
            <div className="mt-4 space-y-6">
              <div ref={qrRef} className="flex justify-center p-6 bg-white border border-[#E2E8F0] rounded-lg">
                <QRCodeSVG value={qrData.qr_content} size={200} level="H" includeMargin={true} />
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between p-3 bg-[#F8FAFC] rounded-lg">
                  <span className="text-[#64748B]">WhatsApp Number</span>
                  <span className="font-mono font-medium">{qrData.whatsapp_number}</span>
                </div>
                <div className="flex justify-between p-3 bg-[#F8FAFC] rounded-lg">
                  <span className="text-[#64748B]">Brand Code</span>
                  <span className="font-mono font-medium">{selectedBrand?.code}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">WhatsApp Link</Label>
                <div className="flex gap-2">
                  <Input value={qrData.whatsapp_link} readOnly className="font-mono text-xs" />
                  <Button variant="outline" onClick={copyLink} className="flex-shrink-0">
                    {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                  </Button>
                </div>
              </div>
              <div className="p-4 bg-[#FEF3C7] rounded-lg text-sm">
                <p className="font-medium text-[#92400E] mb-2">How it works:</p>
                <ol className="list-decimal list-inside text-[#92400E] space-y-1">
                  <li>Customer scans this QR code</li>
                  <li>WhatsApp opens with pre-filled message</li>
                  <li>Customer sends "Hi from {selectedBrand?.code}"</li>
                  <li>System registers customer under {selectedBrand?.name}</li>
                </ol>
              </div>
              <Button className="w-full bg-[#002FA7] hover:bg-[#001F6E] gap-2" onClick={downloadQRFromDialog} data-testid="download-qr-btn">
                <DownloadSimple size={18} />Download QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
